<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useHelpStore } from '@/store/help'
import { watch } from 'vue'
import { useRoute } from 'vue-router'

const helpStore = useHelpStore()
const { t } = useI18n()
const route = useRoute()

// Close the help drawer whenever the route changes to ensure it doesn't stay open when navigating to a different page.
watch(
  () => route.fullPath,
  () => {
    helpStore.close()
  }
)
</script>

<template>
  <aside class="help-panel-shell" :class="{ 'help-panel-shell--open': helpStore.isOpen }">
    <div class="help-panel" :class="{ 'help-panel--open': helpStore.isOpen }">
      <header class="help-panel-header">
        <span class="help-drawer-title">{{ helpStore.content.title ?? '' }}</span>
        <button
          type="button"
          class="help-panel-close"
          aria-label="Close help"
          @click="helpStore.close"
        >
          <FontAwesomeIcon :icon="['fas', 'xmark']" />
        </button>
      </header>

      <div class="help-drawer-body">
        <Image
          v-if="helpStore.content.imgSrc"
          :src="helpStore.content.imgSrc"
          :alt="helpStore.content.title ?? ''"
          class="help-drawer-image"
          width="300"
          preview
        />

        <p v-if="helpStore.content.helpText" class="help-drawer-text">
          {{ helpStore.content.helpText }}
        </p>
      </div>

      <footer v-if="helpStore.content.docsLink" class="help-drawer-footer">
        <a
          :href="helpStore.content.docsLink"
          target="_blank"
          rel="noopener noreferrer"
          class="help-drawer-link"
        >
          <FontAwesomeIcon :icon="['fas', 'arrow-up-right-from-square']" />
          {{ t('global.learnMore', 'Mehr erfahren') }}
        </a>
      </footer>
    </div>
  </aside>
</template>

<style scoped>
.help-panel-shell {
  order: 3;
  flex: 0 0 0;
  width: 0;
  max-width: 0;
  margin-left: -1rem;
  overflow: hidden;
  pointer-events: none;
  transition:
    flex-basis 0.25s ease,
    width 0.25s ease,
    max-width 0.25s ease,
    margin-left 0.25s ease;
}

.help-panel-shell--open {
  flex-basis: min(24rem, 32vw);
  width: min(24rem, 32vw);
  max-width: min(24rem, 32vw);
  margin-left: 0;
  pointer-events: auto;
}

.help-panel {
  height: calc(100vh - 2rem);
  position: sticky;
  top: 1rem;
  background:
    linear-gradient(180deg, rgba(0, 220, 230, 0.08) 0%, rgba(255, 255, 255, 0.98) 18%), #ffffff;
  color: #1f2937;
  border: 1px solid #e8e6e0;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(15, 17, 21, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform: translateX(1.25rem);
  opacity: 0;
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.help-panel--open {
  transform: translateX(0);
  opacity: 1;
}

.help-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1rem 0.85rem;
  border-bottom: 1px solid rgba(0, 40, 100, 0.08);
}

.help-panel-close {
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
  color: var(--tuev-dark-blue, #002864);
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.help-panel-close:hover {
  background: #ffffff;
  border-color: rgba(0, 40, 100, 0.08);
}

.help-drawer-title {
  font-weight: 700;
  font-size: 1.02rem;
  color: var(--tuev-dark-blue, #002864);
  letter-spacing: -0.01em;
}

.help-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  padding: 1rem;
  overflow: auto;
}

.help-drawer-image {
  border-radius: 14px;
  max-width: 100vw;
  border: 1px solid #ece9e3;
  background: #f7f7f5;
  object-fit: contain;
}

.help-drawer-text {
  white-space: pre-line;
  line-height: 1.55;
  margin: 0;
  color: #1f2937;
  font-size: 0.94rem;
}

.help-drawer-footer {
  padding: 0.95rem 1rem 1rem;
  border-top: 1px solid rgba(0, 40, 100, 0.08);
  background: rgba(255, 255, 255, 0.94);
}

.help-drawer-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.25rem;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: #6b6e75;
  text-decoration: none;
  font-weight: 600;
  transition:
    color 0.15s ease,
    transform 0.15s ease;
}

.help-drawer-link:hover {
  color: var(--tuev-dark-blue, #002864);
}

@media (max-width: 768px) {
  .help-panel-shell--open {
    flex-basis: min(20rem, 70vw);
    width: min(20rem, 70vw);
    max-width: min(20rem, 70vw);
  }

  .help-panel {
    height: calc(100vh - 1rem);
    top: 0.5rem;
    border-radius: 16px;
  }

  .help-drawer-body {
    padding: 0.85rem;
  }
}
</style>
